import { NAV_PREFETCH_CACHE_FOR } from "@ui/lib/prefetch-config";
import type { PrefetchProps } from "@ui/lib/prefetch-context";
import { tv } from "@ui/lib/tv";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";

const iconActionLink = tv({
  slots: {
    root: [
      "relative inline-flex size-10 items-center justify-center rounded-full bg-white text-neutral-600 outline-none",
      "border border-neutral-200 transition-colors",
      "[&_svg]:size-5",
      "hover:border-purple-500 hover:bg-purple-25 hover:text-purple-500",
      "focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2",
    ],
    badge:
      "absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-400 px-1 text-[10px] font-semibold leading-none text-white",
  },
});

export interface IconActionLinkProps extends Pick<PrefetchProps, "prefetch" | "cacheFor"> {
  ariaLabel: string;
  /** When > 0, renders a numeric badge (capped at "99+"). */
  badgeCount?: number;
  children: ReactNode;
  href: string;
}

// Defaults to mount prefetch: consumers are persistently visible top-bar
// actions, and Inertia's prefetch cache dedupes repeat mounts in the window.
export function IconActionLink({
  ariaLabel,
  badgeCount = 0,
  cacheFor = NAV_PREFETCH_CACHE_FOR,
  children,
  href,
  prefetch = "mount",
}: IconActionLinkProps) {
  const styles = iconActionLink();
  const hasBadge = badgeCount > 0;

  return (
    <Link aria-label={ariaLabel} cacheFor={cacheFor} className={styles.root()} href={href} prefetch={prefetch}>
      {children}
      {hasBadge ? (
        <span aria-hidden="true" className={styles.badge()}>
          {/* eslint-disable-next-line i18next/no-literal-string -- numeric badge, not translatable */}
          {badgeCount > 99 ? "99+" : badgeCount}
        </span>
      ) : null}
    </Link>
  );
}
